import type { AppConfig } from './types';

// Reinventar brand tokens, aligned with the Reinventar homepage
// (reinventar-service-homepage: static/css/custom.css `:root`, whose ramp is in
// turn mirrored into assets/docs/scss/_variables.scss as `rigreen`/`rilime`) —
// the canonical statement of the brand. The product is the refurbishment and
// resale of furniture and interiors, and its surfaces are white with a
// forest-green accent and a lime highlight, the same green pair
// sirk-service-frontend paints its theme with, so the orgadmin <->
// service-order hop reads as one product.
//
// The display face is All Round Gothic Medium, self-hosted as
// `/apps/reinventar/arg.ttf` and declared in `src/styles/fonts.css` under the
// family name `arg`. Only the one weight exists, so it is a heading face only —
// running copy stays on the system stack, as it does for vardly's Goldman.
const ARG_DISPLAY =
    '"arg", "All Round Gothic", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const SYSTEM_BODY =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const reinventarApp: AppConfig = {
    id: 'reinventar',
    productName: 'Reinventar',
    htmlTitle: 'Admin - Reinventar',
    // The Reinventar app (the service-order surface at app.reinventar.no) is the
    // companion GUI this orgadmin instance manages organizations for.
    appLinkUrl: 'https://app.reinventar.no',
    showBackToApp: true,
    // The homepage's brand mark (assets/images/logos/mark.svg, used in its own
    // header) copied verbatim. It carries its own three brand fills — #60933a,
    // #d8d336, #84b245 — rather than the wordmark's `currentColor` text, which an
    // <img> cannot inherit; that also makes it the only variant legible on both
    // the white app bar and the dark-mode paper the previous solid-black glyph
    // disappeared into.
    logo: { src: '/apps/reinventar/mark.svg', alt: 'Reinventar' },
    favicon: '/apps/reinventar/favicon.png',
    brand: {
        light: {
            primary: { main: '#47702b', light: '#84b245', dark: '#3a5c23', contrastText: '#ffffff' },
            // The lime is the homepage's second brand colour (--ri-lime), the one
            // that plays against the green rather than repeating it. `main` takes
            // the homepage's text-safe step (--ri-lime-dark, 5.5:1 on white)
            // because MUI spends `secondary.main` on outlined chip borders and
            // their labels; the brand lime itself is 1.7:1 on white and can only
            // ever be a fill, so it is carried as `light`.
            secondary: { main: '#6f6b17', light: '#d8d336', dark: '#4d4a10', contrastText: '#ffffff' },
            // White surfaces: the product photographs furniture, and a tinted
            // page ground shifts every thumbnail on it.
            background: { default: '#ffffff', paper: '#ffffff' },
        },
        dark: {
            // Lifted greens on green-tinted near-black. The light primary
            // (#47702b) is far too dark to carry white text on a dark surface,
            // so dark mode climbs the same hue instead of reusing the token.
            primary: { main: '#9ccc6a', light: '#b9dd90', dark: '#7aad4a', contrastText: '#0e1a0b' },
            // The homepage's dark block reassigns --ri-lime-dark to the brand lime:
            // on near-black there is nothing to protect text from, so the lime
            // carries itself.
            secondary: { main: '#d8d336', light: '#eeeb9d', dark: '#b3ae2c', contrastText: '#14140a' },
            background: { default: '#101610', paper: '#172017' },
        },
        fonts: { heading: ARG_DISPLAY, body: SYSTEM_BODY },
        // The homepage hero (.ri-hero): a photograph under a near-black scrim on
        // the brand's pine surface (--ri-surface-inverse). The signup page has no
        // photograph, so the gradient reproduces what the scrim flattens to — the
        // scrim's own #0d1408 at the dark end, climbing to the pine surface
        // underneath it — at the homepage's angle and stops.
        heroGradient: 'linear-gradient(100deg, #0d1408 0%, #1a2b10 42%, #2c471b 72%, #3a5c23 100%)',
        // The hero is dark in both appearances even though the app is
        // light-first, so the signup page paints its foreground with the dark
        // palette while the admin shell and the other pre-auth pages stay light.
        // (White is 7.7:1 and `text.secondary` 4.7:1 against the lightest stop.)
        heroThemeMode: 'dark',
    },
    defaultThemeMode: 'light',
    // Org self-management plus the resale/refurbishment surface: solution
    // deliveries (products and services are the core of what Reinventar sells),
    // the furniture taxonomy those deliveries are described in, and the process
    // resource taxonomy (category/resource) the delivery processes draw on —
    // that tree lives here, not on sirktek. Which of
    // these a user actually sees is decided by the JWT permissions their tier
    // confers — the REINVENTAR_* plans sell products/services and, directly on
    // their own ladder, categories and locations, both from rank 1 (see
    // PERMISSION_SOLD_BY and SubscriptionLevel.java). No plan includes another
    // app's rank any more: assets are not a reinventar capability at any tier.
    // An organization that needs asset management buys a separate sirktek
    // entitlement and uses the sirktek app for it — a reinventar-app session's
    // JWT is scoped to the reinventar OIDC client and never carries sirktek
    // permissions, even for an organization that holds both entitlements.
    enabledResources: new Set([
        'settings',
        'billing',
        'user',
        'api-key',
        // Organization sharing, on the same gate as sirktek and vardly: the
        // resource requires write:organization, which no app sells (see
        // PERMISSION_SOLD_BY), so every reinventar tier carries it.
        'access-grant',
        'product',
        'service',
        // Procurement subscriptions — what this organization wants to hear about
        // from the public procurement feed (change: procurement-subscriptions).
        // Gated on `<org>:write:procurements` (WRITE_PROCUREMENTS in
        // sirk-orgadmin-backend's Permission.java, sold by App.REINVENTAR at rank 3
        // — so of the three reinventar tiers only REINVENTAR_MILJOREGNSKAP confers
        // it; this line decides the build, the token decides the customer). Served by
        // sirk-procurement-backend on its own host, not by sirk-orgadmin-backend —
        // see helpers/procurementDataProvider.ts. Reinventar only: the anbud views
        // this configures live in sirk-service-frontend, this app's companion.
        'procurement-subscription',
        'category/location',
        'category/furniture',
        'category/resource',
        'category/model',
        'category/manufacturer',
    ]),
    signup: {
        // Tier ids must match the backend's SubscriptionLevel constants exactly:
        // the id is posted verbatim as `subscriptionLevel` at signup, and a value
        // no App claims creates no entitlement at all. Every tier is billed per
        // step of 100 active/stored objects — the next 100 objects cost the same
        // tier price again. An "object" is any object the app manages, whichever
        // category tree it sits in, phrased in the tier copy as the whole catalog
        // rather than an enumeration so adding a tree does not silently change
        // what customers were quoted. Display only — the backend does not meter
        // steps yet.
        tiers: [
            {
                id: 'REINVENTAR_TJENESTER',
                i18nKey: 'reinventarTjenester',
                prices: { NOK: 2250 },
                featureCount: 4,
                highlighted: false,
                metered: { objects: 100 },
            },
            {
                id: 'REINVENTAR_API_AI',
                i18nKey: 'reinventarApiAi',
                prices: { NOK: 2750 },
                featureCount: 3,
                highlighted: true,
                metered: { objects: 100 },
            },
            {
                id: 'REINVENTAR_MILJOREGNSKAP',
                i18nKey: 'reinventarMiljoregnskap',
                prices: { NOK: 4250 },
                featureCount: 3,
                highlighted: false,
                metered: { objects: 100 },
            },
        ],
        // Norway only. Matches `countries.app.reinventar` in sirk-orgadmin-backend,
        // which is the enforcing gate; this list decides what the picker offers.
        countries: ['no'],
    },
    // Reinventar refurbishes furniture and interiors, so a fresh tenant is opted
    // into the furniture taxonomy — the same default sirktek carries.
    defaultTaxonomies: ['furniture'],
};
